// Image360Viewer.jsx
import React, { useState, useRef, useEffect } from "react";

// Utility to zero-pad numbers: pad(5, 3) -> "005"
const pad = (num, size) => {
  const s = String(num);
  return s.length >= size ? s : "0".repeat(size - s.length) + s;
};

export default function Image360Viewer({
  path = "/images/360/",    // folder path (public folder)
  filePrefix = "frame-",    // prefix e.g. "frame-"
  fileExt = ".jpg",         // extension e.g. ".jpg" or ".webp"
  frameCount = 36,          // number of frames
  padLength = 3,            // digits in file name, e.g. 3 for 001..036
  width = "100%",           // ✅ must be string if using %
  height = "100%",          // ✅ same here
  initialFrame = 0,
  autoplay = false,
  autoplaySpeed = 60        // ms between frames when autoplay on
}) {
  const [index, setIndex] = useState(() => {
    if (initialFrame < 0) return 0;
    if (initialFrame >= frameCount) return frameCount - 1;
    return Math.floor(initialFrame);
  });
  const draggingRef = useRef(false);
  const lastXRef = useRef(null);
  const rafRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const imagesRef = useRef([]);

  // Build array of image URLs
  const buildUrls = () => {
    const arr = [];
    for (let i = 1; i <= frameCount; i++) {
      arr.push(`${path}${filePrefix}${pad(i, padLength)}${fileExt}`);
    }
    return arr;
  };

  const urls = buildUrls();

  // Preload images
  useEffect(() => {
    let mounted = true;
    const imgs = [];
    let loadedCount = 0;
    urls.forEach((u, i) => {
      const img = new Image();
      img.onload = img.onerror = () => {
        loadedCount++;
        if (!mounted) return;
        if (loadedCount === urls.length) {
          imagesRef.current = imgs;
          setLoaded(true);
        }
      };
      img.src = u;
      imgs[i] = img;
    });
    return () => (mounted = false);
  }, [urls]);

  // Handlers
  const onPointerDown = (e) => {
    draggingRef.current = true;
    lastXRef.current = getClientX(e);
    e.preventDefault();
  };

  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    const x = getClientX(e);
    const dx = x - lastXRef.current;
    const sensitivity = 6;
    if (Math.abs(dx) >= 1) {
      const steps = Math.floor(dx / sensitivity);
      if (steps !== 0) {
        updateIndexBy(-steps);
        lastXRef.current = x;
      }
    }
  };

  const onPointerUp = () => {
    draggingRef.current = false;
    lastXRef.current = null;
  };

  const onWheel = (e) => {
    const delta = Math.sign(e.deltaY);
    updateIndexBy(delta);
  };

  const updateIndexBy = (delta) => {
    setIndex((prev) => {
      const next = (prev + delta) % frameCount;
      return next < 0 ? next + frameCount : next;
    });
  };

  function getClientX(e) {
    if (e.touches?.length) return e.touches[0].clientX;
    if (e.changedTouches?.length) return e.changedTouches[0].clientX;
    return e.clientX ?? 0;
  }

  // Autoplay loop
  useEffect(() => {
    if (!autoplay) return;
    let mounted = true;
    const step = () => {
      setIndex((p) => (p + 1) % frameCount);
      if (mounted) rafRef.current = setTimeout(step, autoplaySpeed);
    };
    rafRef.current = setTimeout(step, autoplaySpeed);
    return () => {
      mounted = false;
      if (rafRef.current) clearTimeout(rafRef.current);
    };
  }, [autoplay, autoplaySpeed, frameCount]);

  return (
    <div
      className="image-360-wrapper"
      style={{  touchAction: "none", userSelect: "none" }}
      onMouseDown={onPointerDown}
      onMouseMove={onPointerMove}
      onMouseUp={onPointerUp}
      onMouseLeave={onPointerUp}
      onTouchStart={onPointerDown}
      onTouchMove={onPointerMove}
      onTouchEnd={onPointerUp}
      onWheel={onWheel}
      role="img"
      aria-label="360 image viewer"
    >
      {!loaded && <div className="image-360-loading">Loading...</div>}
      <img
        src={urls[index]}
        alt={`frame ${index + 1}`}
        draggable={false}
        style={{
          display: loaded ? "block" : "none",
          width: "100%",
          height: "100%",
          objectFit: "contain"
        }}
      />
    </div>
  );
}
